import React, { useEffect, useMemo, useState } from "react";
import { Wallet, RefreshCw, Download, Info, Link as LinkIcon } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/Header";
import HelpFab from "@/components/HelpFab";

const usd0 = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;
const usd2 = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (n, dec = 2) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: dec });
const ago = (iso) => {
  if (!iso) return "never";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 2) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const h = Math.floor(mins / 60);
  return h < 24 ? `${h}h ago` : `${Math.floor(h / 24)}d ago`;
};

const COLS = [
  { k: "name", label: "Wallet" },
  { k: "staking_locked", label: "Staked · locked", right: true, ca: true },
  { k: "staking_free", label: "Staked · free", right: true, ca: true },
  { k: "mining_claimable", label: "Mining CA", right: true, ca: true },
  { k: "wallet_ca", label: "CA in wallet", right: true, ca: true },
  { k: "bot_total", label: "BOT", right: true, ca: true },
  { k: "sellable_usd", label: "Sellable now", right: true },
  { k: "locked_usd", label: "Locked", right: true },
  { k: "total_usd", label: "Everything", right: true },
];

/** The sell-everything page: the headline, then every wallet behind it. */
export default function LiquidValue() {
  const { user } = useAuth();
  const [scope, setScope] = useState(() => localStorage.getItem("portfolio_sel") || "downline");
  const [portfolios, setPortfolios] = useState([]);
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [priceOverride, setPriceOverride] = useState("");
  const [sort, setSort] = useState({ k: "total_usd", dir: "desc" });

  useEffect(() => { api.get("/portfolios").then((r) => setPortfolios(r.data || [])).catch(() => {}); }, []);

  const load = (refresh = 0) => {
    setBusy(true);
    const params = { portfolio: scope, refresh };
    if (Number(priceOverride) > 0) params.ca_price = Number(priceOverride);
    api.get("/liquid-value", { params })
      .then((r) => { setD(r.data); setErr(""); })
      .catch((e) => setErr(e.response?.data?.detail || "Couldn't read the balances"))
      .finally(() => setBusy(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setD(null); load(0); }, [scope]);

  const pick = (id) => { setScope(id); try { localStorage.setItem("portfolio_sel", id); } catch { /* private mode */ } };

  const rows = useMemo(() => {
    const list = (d?.wallets || []).map((w) => ({
      ...w,
      wallet_ca: w.ca.wallet,
      staking_locked: w.ca.staking_locked,
      staking_free: w.ca.staking_free,
      mining_claimable: w.ca.mining_claimable,
      bot_total: w.bot.total,
    }));
    const dir = sort.dir === "asc" ? 1 : -1;
    return list.sort((a, b) => {
      const x = a[sort.k]; const y = b[sort.k];
      if (typeof x === "string" || typeof y === "string") return String(x || "").localeCompare(String(y || "")) * dir;
      return ((x || 0) - (y || 0)) * dir;
    });
  }, [d, sort]);

  const csv = () => {
    const head = ["Wallet", "Address", "Staked locked CA", "Staked free CA", "Mining CA", "Community CA",
      "CA in wallet", "CA total", "BOT pledged", "BOT claimable", "BOT in wallet", "BOT total",
      "Sellable USD", "Locked USD", "Everything USD", "Chain read"];
    const lines = (d?.wallets || []).map((w) => [
      w.name, w.address, w.ca.staking_locked, w.ca.staking_free, w.ca.mining_claimable, w.ca.community_claimable,
      w.ca.wallet, w.ca.total, w.bot.pledged, w.bot.rewards_claimable, w.bot.wallet, w.bot.total,
      w.sellable_usd, w.locked_usd, w.total_usd, w.chain_read_at || "",
    ]);
    const body = [head, ...lines].map((r) => r.map((c) => `"${String(c ?? "")}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([body], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `liquid-value-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const named = portfolios.filter((p) => !p.leader_showcase);
  const isAdmin = user?.role === "admin";

  return (
    <div className="min-h-screen pb-24" data-testid="liquid-value-page">
      <Header />
      <div className="px-6 md:px-10 pt-8">
        <div className="max-w-[1500px] mx-auto">
          <div className="pill mb-4" style={{ borderColor: "rgba(0,134,58,0.3)" }}>
            <span className="dot" style={{ background: "#00863A" }} /> Sell-everything value
          </div>
          <h1 className="font-display text-3xl md:text-5xl font-semibold leading-[1.1] tracking-tight">
            Every CA and BOT you hold, <span style={{ color: "#00863A" }}>at today&apos;s price</span>.
          </h1>
          <p className="text-base text-ink-2 max-w-3xl mt-3">
            Staking, mining, community rewards and whatever is sitting in the wallets themselves — added up once,
            never twice, and split into what could actually be sold this morning.
          </p>

          <div className="flex flex-wrap items-center gap-2 mt-5">
            <button type="button" onClick={() => pick("wallet")} data-testid="liquid-scope-wallet"
              className="pill text-xs" style={scope === "wallet" ? { background: "#00863A", color: "white", borderColor: "#00863A" } : undefined}>
              This Account
            </button>
            <button type="button" onClick={() => pick("downline")} data-testid="liquid-scope-downline"
              className="pill text-xs" style={scope === "downline" ? { background: "#00863A", color: "white", borderColor: "#00863A" } : undefined}>
              All wallets
            </button>
            {isAdmin && named.map((p) => (
              <button key={p.id} type="button" onClick={() => pick(p.id)} data-testid={`liquid-scope-${p.id}`}
                className="pill text-xs" style={scope === p.id ? { background: "#00863A", color: "white", borderColor: "#00863A" } : undefined}>
                {p.name}
              </button>
            ))}
            <div className="ml-auto flex items-center gap-2">
              <input value={priceOverride} onChange={(e) => setPriceOverride(e.target.value.replace(/[^0-9.]/g, ""))}
                onBlur={() => load(0)} onKeyDown={(e) => e.key === "Enter" && load(0)}
                placeholder={d ? `CA $${Number(d.live_ca_price).toFixed(2)}` : "CA price"}
                className="w-28 px-3 py-1.5 rounded-xl bg-white/65 border text-xs font-mono text-right"
                style={{ borderColor: "rgba(15,18,38,0.10)" }} data-testid="liquid-price-override" title="Try another CA price" />
              <button type="button" onClick={() => load(1)} disabled={busy} className="pill text-xs inline-flex items-center gap-1.5" data-testid="liquid-refresh">
                <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Re-read chain
              </button>
              <button type="button" onClick={csv} disabled={!d} className="pill text-xs inline-flex items-center gap-1.5" data-testid="liquid-csv">
                <Download size={12} /> CSV
              </button>
            </div>
          </div>

          {err && <div className="glass p-4 mt-5 text-sm text-rose-700" data-testid="liquid-error">{err}</div>}
          {!d && !err && <div className="glass p-6 mt-5 text-sm text-ink-3" data-testid="liquid-loading">Reading balances…</div>}

          {d && (
            <>
              <div className="grid sm:grid-cols-3 gap-4 mt-5">
                <div className="glass-strong p-5" data-testid="liquid-kpi-sellable">
                  <div className="kpi-label mb-1.5">Sellable right now</div>
                  <div className="font-display text-3xl font-bold tracking-tight tabular-nums" style={{ color: "#00863A" }}>{usd0(d.sellable_usd)}</div>
                  <div className="text-[11px] text-ink-3 mt-1">
                    {num(d.ca.staking_free + d.ca.mining_claimable + d.ca.community_claimable + d.ca.wallet)} CA +{" "}
                    {num(d.bot.rewards_claimable + d.bot.wallet)} BOT, claimable or already in hand
                  </div>
                </div>
                <div className="glass p-5" data-testid="liquid-kpi-locked">
                  <div className="kpi-label mb-1.5">Still locked in terms</div>
                  <div className="font-display text-3xl font-bold tracking-tight tabular-nums text-ink-2">{usd0(d.locked_usd)}</div>
                  <div className="text-[11px] text-ink-3 mt-1">
                    {num(d.ca.staking_locked)} CA staked + {num(d.bot.pledged)} BOT pledged, unlocking daily
                  </div>
                </div>
                <div className="glass p-5" data-testid="liquid-kpi-total">
                  <div className="kpi-label mb-1.5">Everything, once unlocked</div>
                  <div className="font-display text-3xl font-bold tracking-tight tabular-nums" style={{ color: "#4060FF" }}>{usd0(d.total_usd)}</div>
                  <div className="text-[11px] text-ink-3 mt-1">
                    {num(d.ca.total)} CA × ${Number(d.ca_price).toFixed(2)} + {num(d.bot.total)} BOT × ${Number(d.bot_price).toFixed(2)}
                    {!d.price_is_live && <span style={{ color: "#B45309" }}> · your price, not the live one</span>}
                  </div>
                </div>
              </div>

              <div className="glass p-5 mt-5" data-testid="liquid-buckets">
                <div className="text-xs font-mono uppercase tracking-wider text-ink-3 mb-3">Where the CA and BOT sit</div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-2.5 text-sm">
                  {[
                    ["Staked, still locked", d.ca.staking_locked, "CA", d.ca.staking_locked * d.ca_price],
                    ["Staked, free to take", d.ca.staking_free, "CA", d.ca.staking_free * d.ca_price],
                    ["CA mining claimable", d.ca.mining_claimable, "CA", d.ca.mining_claimable * d.ca_price],
                    ["Community CA claimable", d.ca.community_claimable, "CA", d.ca.community_claimable * d.ca_price],
                    ["CA in the wallets", d.ca.wallet, "CA", d.ca.wallet * d.ca_price],
                    ["BOT pledged", d.bot.pledged, "BOT", d.bot.pledged * d.bot_price],
                    ["BOT rewards claimable", d.bot.rewards_claimable, "BOT", d.bot.rewards_claimable * d.bot_price],
                    ["BOT in the wallets", d.bot.wallet, "BOT", d.bot.wallet * d.bot_price],
                  ].map(([label, amount, unit, value]) => (
                    <div key={label} className="flex items-baseline justify-between gap-3 border-b pb-1.5" style={{ borderColor: "rgba(15,18,38,0.06)" }}>
                      <span className="text-ink-2 text-[12px]">{label}</span>
                      <span className="font-mono text-xs tabular-nums whitespace-nowrap">
                        {num(amount, 2)} <span className="text-ink-3">{unit}</span>
                        <span className="ml-2 font-semibold">{usd0(value)}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="glass p-5 mt-5 overflow-x-auto" data-testid="liquid-table">
                <div className="flex items-center gap-3 mb-3 flex-wrap">
                  <Wallet size={14} style={{ color: "#00863A" }} />
                  <div className="text-xs font-mono uppercase tracking-wider text-ink-3">Wallet by wallet</div>
                  <div className="ml-auto text-[11px] text-ink-3 inline-flex items-center gap-1.5">
                    <LinkIcon size={11} /> chain balances read {ago(d.chain.newest)}
                    {d.chain.read < d.chain.wallets && ` · ${d.chain.wallets - d.chain.read} still to read`}
                  </div>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[11px] font-mono uppercase tracking-wider text-ink-3">
                      {COLS.map((c) => (
                        <th key={c.k} className={`py-1.5 ${c.right ? "text-right pl-4" : "text-left pr-4"} cursor-pointer select-none hover:text-ink`}
                          onClick={() => setSort({ k: c.k, dir: sort.k === c.k && sort.dir === "desc" ? "asc" : "desc" })}
                          data-testid={`liquid-sort-${c.k}`}>
                          {c.label}{sort.k === c.k ? (sort.dir === "desc" ? " ↓" : " ↑") : ""}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((w) => (
                      <tr key={w.node_id} className="border-t" style={{ borderColor: "rgba(15,18,38,0.06)" }} data-testid={`liquid-row-${w.node_id}`}>
                        <td className="py-2 pr-4">
                          <div className="font-medium truncate max-w-[180px]">{w.name || "—"}</div>
                          <div className="font-mono text-[10px] text-ink-3">{String(w.address || "").slice(0, 10)}…</div>
                        </td>
                        <td className="py-2 pl-4 text-right font-mono text-xs tabular-nums text-ink-3">{num(w.staking_locked)}</td>
                        <td className="py-2 pl-4 text-right font-mono text-xs tabular-nums">{num(w.staking_free)}</td>
                        <td className="py-2 pl-4 text-right font-mono text-xs tabular-nums">{num(w.mining_claimable)}</td>
                        <td className="py-2 pl-4 text-right font-mono text-xs tabular-nums" style={{ color: w.wallet_ca > 0 ? "#4060FF" : undefined }}>{num(w.wallet_ca)}</td>
                        <td className="py-2 pl-4 text-right font-mono text-xs tabular-nums" style={{ color: w.bot_total > 0 ? "#0E7490" : undefined }}
                          title={`${num(w.bot.pledged, 4)} pledged · ${num(w.bot.rewards_claimable, 4)} claimable · ${num(w.bot.wallet, 4)} in wallet${w.bot.read ? "" : " · BOT mining account never read"}`}>
                          {num(w.bot_total)}{!w.bot.read && <span className="text-ink-3 ml-0.5" title="BOT mining account never read">*</span>}
                        </td>
                        <td className="py-2 pl-4 text-right font-mono text-xs tabular-nums font-semibold" style={{ color: "#00863A" }}>{usd2(w.sellable_usd)}</td>
                        <td className="py-2 pl-4 text-right font-mono text-xs tabular-nums text-ink-3">{usd2(w.locked_usd)}</td>
                        <td className="py-2 pl-4 text-right font-mono text-xs tabular-nums font-bold">{usd2(w.total_usd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="glass p-4 mt-5 flex items-start gap-3" data-testid="liquid-method">
                <Info size={15} style={{ color: "#00863A" }} className="flex-shrink-0 mt-0.5" />
                <p className="text-xs text-ink-2 leading-relaxed">
                  <b>How this is counted.</b> CaryPact&apos;s My Staking balance already contains the flexible
                  principal, the principal a fixed term has released and the rewards compounded so far — so staking
                  is counted once and the claimable figures only split it into free and locked, never added on top.
                  CA and BOT sitting in the wallets come straight off BOT Chain, which answers for every address
                  whether or not its CaryPact session is still alive.
                  {" "}<b>Left out on purpose:</b> {usd0(d.excluded.hashrate_usd)} of hashrate,{" "}
                  {usd0(d.excluded.miner_usd)} of miners and {num(d.excluded.power, 0)} of power — that money bought
                  the income and can&apos;t be sold back.
                  {d.bot_unread > 0 && ` ${d.bot_unread} wallet${d.bot_unread === 1 ? "" : "s"} marked * have never had their BOT mining account read, so claimable BOT there isn't in the total.`}
                  {" "}Prices move constantly; this is a snapshot, not a quote, and selling in size would move the price itself.
                </p>
              </div>
            </>
          )}
        </div>
      </div>
      <HelpFab />
    </div>
  );
}
